// Generated-client detail query for a deep-linked agent-version Drawer.
// Loaded list rows seed the query; a cold `?version=` link falls back to the
// flat `/v1/agent-versions/{id}` Get. The selected agent remains authoritative
// even for same-organization users who may read another agent's version.

import { useQuery } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { AgentVersionDetailDrawerView } from "./AgentVersionDetailDrawerView";

type AgentVersion = components["schemas"]["AgentVersionResponse"];

export interface AgentVersionDetailDrawerProps {
  agentId: string;
  loadedVersions: AgentVersion[];
  onDismiss: () => void;
  versionId: string | null;
}

export function AgentVersionDetailDrawer({
  agentId,
  loadedVersions,
  onDismiss,
  versionId,
}: AgentVersionDetailDrawerProps) {
  const loaded = loadedVersions.find((version) => version.id === versionId);
  const detail = useQuery({
    enabled: versionId !== null && loaded === undefined,
    initialData: loaded,
    queryKey: queryKeys.agentVersions.detail(versionId ?? "pending"),
    queryFn: async () => {
      if (versionId === null) {
        throw new Error("Agent version id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/agent-versions/{id}", {
        params: { path: { id: versionId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const version = loaded ?? detail.data;
  const scopeError =
    version !== undefined && version.agent_id !== agentId
      ? "Agent version not found for this agent."
      : undefined;
  const errorMessage =
    scopeError ??
    (detail.isError
      ? requestErrorDescription(detail.error, "Agent version not found.")
      : undefined);

  return (
    <AgentVersionDetailDrawerView
      errorMessage={errorMessage}
      isOpen={versionId !== null}
      isPending={versionId !== null && loaded === undefined && detail.isPending}
      onDismiss={onDismiss}
      version={scopeError === undefined ? version : undefined}
    />
  );
}
